import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
// @mui
import { Box, Stack, Drawer, Grid, IconButton, Button } from '@mui/material';
import Iconify from 'src/components/iconify/Iconify';
// hooks
import useResponsive from '../../../hooks/useResponsive';
// config
import { NAV, CLIENT_PORTAL_ORIGIN } from '../../../config-global';
// components
import { Logo } from '../../../components/logo';
import Scrollbar from '../../../components/scrollbar';
import { NavSectionVertical } from '../../../components/nav-section';
//
import useNavConfig from './config-navigation';
// import NavDocs from './NavDocs';
import NavAccount from './NavAccount';
import NavToggleButton from './NavToggleButton';

// ----------------------------------------------------------------------

type Props = {
  openNav: boolean;
  onCloseNav: VoidFunction;
};

export default function NavVertical({ openNav, onCloseNav }: Props) {
  const { pathname } = useLocation();
  const isDesktop = useResponsive('up', 'lg');
  const navConfig = useNavConfig();
  const token = localStorage.getItem('accessToken');
  const user = localStorage.getItem('user');
  const userString = user ? JSON.parse(user) : null;

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
  }, [pathname, openNav, onCloseNav]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Stack
        spacing={3}
        sx={{
          pt: 3,
          pb: 2,
          px: 2.5,
          flexShrink: 0,
        }}
      >
        <Grid container>
          <Grid
            container
            item
            xs={12}
            md={12}
            lg={12}
            xl={12}
            alignItems="center"
            justifyContent="flex-start"
          >
            <Grid item lg="auto" md={9.5} sm={9.5} xs={9.5}>
              <Logo />
            </Grid>

            <Grid
              item
              xs="auto"
              md="auto"
              lg="auto"
              xl="auto"
              display={isDesktop ? 'none' : 'flex'}
            >
              <IconButton onClick={onCloseNav} sx={{ color: 'text.primary' }}>
                <Iconify icon="solar:arrow-right-broken" width={25} />
              </IconButton>
            </Grid>
          </Grid>
        </Grid>
        <NavAccount />
        {userString?.role === 'SuperAdmin' &&
          <Grid
            item
            lg="auto"
            md={9.5}
            sm={9.5}
            xs={9.5}
            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          >
            <Button
              style={{ width: '100%' }}
              onClick={() => {
                const portalOrigin = CLIENT_PORTAL_ORIGIN;
                // Use hash (fragment) instead of query string to avoid HTTP 431 (Request Header Fields Too Large)
                // — the server never receives the hash, so the request URL stays short.
                const params = new URLSearchParams();
                if (token) params.set('adminToken', token);
                if (userString) params.set('user', JSON.stringify(userString));
                const hash = params.toString();
                const portalUrl = `${portalOrigin}/auth/company/list${hash ? `#${hash}` : ''}`;
                window.open(portalUrl, '_blank');
                // Client portal must read adminToken & adminUser from URL on load (see src/utils/adminAuthForClientPortal.ts)
              }}
            >
              Open Client Portal
            </Button>
          </Grid>
        }
      </Stack>

      <NavSectionVertical data={navConfig} />

      <Box sx={{ flexGrow: 1 }} />
      {/* <NavDocs /> */}
    </Scrollbar>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV.W_DASHBOARD },
      }}
    >
      <NavToggleButton />

      {isDesktop ? (
        <Drawer
          open
          variant="permanent"
          PaperProps={{
            sx: {
              zIndex: 0,
              width: NAV.W_DASHBOARD,
              bgcolor: 'transparent',
              borderRightStyle: 'dashed',
            },
          }}
        >
          {renderContent}
        </Drawer>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: {
              width: NAV.W_DASHBOARD,
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
